/* eslint-disable react-hooks/exhaustive-deps */
const React = require("react");

const {
  useEffect,
  useState,
  useCallback,
  useMemo,
  memo,
  createElement,
  useRef,
} = React;

const PropTypes = require("prop-types");

const classnames = require("classnames");
const I18n = require("frontend-i18n");

// const {
//     querySelectorDeep,
// } = require("./utils/query-selector-shadow-dom/querySelectorDeep");
const useManifest = require("./hooks/useManifest");
const ModuleContainer = require("./components/ModuleContainer");
const { processManifestScripts } = require("./utils/resources");
const {
  MODULE_ASSET_PREFIX,
  REMOTE_MODULE_CLASS_NAME,
  SCRIPT_ERROR_EVENT_KEY,
} = require("../config/constants");

const Module = memo((props) => {
  const {
    id,
    name,
    host,
    path,
    root,
    vendor,
    className,
    style,
    loadingComponent,
    errorComponent,
    isolated,
    manifestCache,
    restClientConfig,
    shadowRootConfig,
    manifestStoreConfig,
    lifeCycleEvents,
    ...extraProps
  } = props;

  const moduleInfo = useMemo(
    () => ({ id, name, host, path, root }),
    [id, name, host, path, root]
  );

  const {
    onInit: onInitLifeCycle,
    onFinish: onFinishLifeCycle,
    onTimeout,
    onModuleReady,
    onModuleError,
    onModuleFailure,
    onModuleTimeout,
    config: lifeCycleConfig,
  } = lifeCycleEvents || {};

  const { resetFn } = manifestStoreConfig;

  // Manifest
  const {
    appData,
    error,
    getComponentMethod,
    // deleteComponentMethod,
    onLoadedModule,
    clearStore,
    dispatchEvent: dispatchManifestEvt,
  } = useManifest({
    consumer: process.env.REMOTE_MODULE_APP_NAME,
    host,
    path,
    root,
    name,
    cache: manifestCache,
    restClientConfig,
    id,
  });

  // Identifiers
  const identifier = useMemo(
    () => (appData ? `${appData.manifest.APP_NAME}-${name}` : name),
    [appData, name]
  );

  const containerId = useMemo(
    () => `remote-module-container-${identifier}-${id}`,
    [appData, id]
  );

  const appName = useRef();
  const ref = useRef();
  const refRoot = useRef();
  const moduleReadyRef = useRef(false);
  const frmReadyRef = useRef(false);

  const moduleClassNames = useMemo(
    () => classnames(className, `${REMOTE_MODULE_CLASS_NAME} ${identifier}`),
    [className, identifier]
  );

  const mainScriptId = useMemo(() => `${identifier}-script`, [identifier]);

  const [renderError, setRenderError] = useState();
  const [loadedModule, setLoadedModule] = useState();

  // const removeModuleScripts = useCallback(
  //     ({ mainId, vendorId }) => {
  //         const mountedComponent = querySelectorDeep(
  //             `section[data-script='${mainId}']`,
  //             document
  //         ); // TODO: replace deep for library and build.

  //         if (!mountedComponent) {
  //             try {
  //                 const vendorScript = document.getElementById(vendorId);

  //                 if (vendorScript) {
  //                     vendorScript.remove();
  //                 }
  //             } catch (e) {
  //                 console.error(
  //                     `Error removing vendor script "${vendorId}": ${e}`
  //                 );
  //             }

  //             try {
  //                 const mainScript = document.getElementById(mainId);

  //                 if (mainScript) {
  //                     mainScript.remove();
  //                 }
  //             } catch (e) {
  //                 console.error(
  //                     `Error removing main script "${mainId}": ${e}`
  //                 );
  //             }

  //             try {
  //                 deleteComponentMethod();
  //             } catch (e) {
  //                 console.error(
  //                     `Error removing render method "${identifier}": ${e}`
  //                 );
  //             }
  //         }
  //     },
  //     [identifier]
  // );

  const renderModule = useCallback(() => {
    try {
      let moduleTimeout;

      const {
        i18n = { translations: {} },
        security,
        ...runtimeProps
      } = appData.runtimeProps || {};

      const {
        createRoot: moduleCreateRoot,
        createElement: moduleCreateElement,
        ModuleContext,
        Component,
      } = loadedModule;

      if (!renderError) {
        while (ref.current && ref.current.firstChild) {
          try {
            ref.current.firstChild.remove();
          } catch (e) {
            console.error(`Error removing ${identifier} child: ${e}`);
          }
        }

        if (isolated) {
          try {
            ref.current.attachShadow({
              mode: "open",
              ...shadowRootConfig,
            });
          } catch (e) {
            console.error(`Error attaching shadow root to ${identifier}: ${e}`);
          }
        }

        const styleUrl = appData.manifest[`${MODULE_ASSET_PREFIX}${name}.css`];

        const moduleStyle = moduleCreateElement(
          "style",
          { key: `${containerId}-style` },
          `@import '${styleUrl}'`
        );

        const onModuleReadyCb = (arg = {}) => {
          const { payload } = arg;
          moduleReadyRef.current = true;
          clearTimeout(moduleTimeout);

          if (typeof onModuleReady === 'function') {
            onModuleReady({
              moduleInfo,
              payload,
            });
          }
        };

        const onModuleErrorCb = (arg = {}) => {
          const { payload, error: moduleError }  = arg;
          moduleReadyRef.current = true;

          if (typeof onModuleError === 'function') {
            onModuleError({
              moduleInfo,
              payload,
              error: moduleError,
            });
          }
        };

        const onModuleFailureCb =  (arg = {}) => {
          const { payload, error: moduleError }  = arg;

          if (typeof onModuleFailure === 'function') {
            onModuleFailure?.({
              moduleInfo,
              payload,
              error: moduleError,
            });
          }
        };

        const component = moduleCreateElement(Component, {
          key: identifier,
          security,
          ...appData.resolvedProps,
          ...extraProps,
        });

        const module = moduleCreateElement(
          ModuleContext.Provider,
          {
            value: {
              id,
              name,
              host,
              path,
              root,
              vendor,
              container: ref.current,
              isolated,
              hasResolvedProps: appData.resolvedProps !== undefined,
              i18n: new I18n(i18n),
              lifeCycleEvents: {
                onReady: onModuleReadyCb,
                onError: onModuleErrorCb,
                onCriticalFailure: onModuleFailureCb,
              },
              ...runtimeProps,
            },
          },
          styleUrl ? [moduleStyle, component] : component
        );

        refRoot.current = moduleCreateRoot(
          ref.current.shadowRoot ? ref.current.shadowRoot : ref.current
        );

        refRoot.current.render(module);

        // Finishing lifecycle
        onFinishLifeCycle?.({ moduleInfo });
        frmReadyRef.current = true;

        const timeoutLimit = lifeCycleConfig?.module?.timeout;

        if (timeoutLimit) {
          moduleTimeout = setTimeout(() => {
            if (!moduleReadyRef.current) {
              onModuleTimeout?.({ moduleInfo });
            }
          }, timeoutLimit);
        }
      }
    } catch (e) {
      console.error(`Error rendering ${identifier}: ${e}`);
      setRenderError(`Cannot render ${identifier}.`);
    }
  }, [loadedModule, ref.current, isolated]);

  useEffect(() => {
    let frmTimeout;

    // Initialize lifecycle
    onInitLifeCycle?.({ moduleInfo });

    if (lifeCycleConfig?.frm?.timeout) {
      const timeoutLimit = lifeCycleConfig.frm.timeout;

      // istanbul ignore next
      frmTimeout = setTimeout(() => {
        if (!frmReadyRef.current) {
          onTimeout?.({
            moduleInfo,
            payload: `Timeout limit of ${timeoutLimit}`,
          });
        }
      }, timeoutLimit);
    }

    return () => {
      clearTimeout(frmTimeout);
      if (refRoot.current) {
        try {
          setTimeout(() => refRoot.current.unmount());
        } catch (e) {
          console.error(`Error unmounting ${containerId}: ${e}`);
        }
      }
    };
  }, []);

  useEffect(() => {
    if (error) {
      setRenderError(error);
    }
  }, [error]);

  useEffect(() => {
    if (renderError) {
      frmReadyRef.current = true;
      onFinishLifeCycle?.({ moduleInfo, error: renderError });
    }
  }, [renderError]);

  const loadScriptsErrorCb = useCallback(
    ({ message }) => {
      if (resetFn) {
        clearStore();
        resetFn();
      }

      setRenderError(message);
      dispatchManifestEvt(SCRIPT_ERROR_EVENT_KEY, { error: message });
    },
    [clearStore, resetFn]
  );

  const loadScriptsCb = useCallback(() => {
    const componentData = getComponentMethod();

    if (componentData) {
      setLoadedModule(componentData);
      onLoadedModule(appData);
    } else {
      console.error(`Error loading module component: ${name}`);
      setRenderError("Cannot find module component.");
    }
  }, [appData, getComponentMethod]);

  useEffect(() => {
    if (appData) {
      const { manifest: { APP_NAME } = {} } = appData;

      appName.current = APP_NAME;

      const loadModuleScripts = processManifestScripts({
        manifest: appData ? appData.manifest : undefined,
        errorCallback: loadScriptsErrorCb,
        name,
        vendor,
        identifier,
      });

      void loadModuleScripts(loadScriptsCb);

      // HOTFIX: prevent memory leak
      // const scriptsIdentifier = `${appData.manifest.APP_NAME}-${name}`;
      // return () => {
      //   removeModuleScripts({ mainId: `${scriptsIdentifier}-script`, vendorId: `${scriptsIdentifier}-vendor` });
      // }
    }
  }, [appData]);

  useEffect(() => {
    if (loadedModule && ref.current) {
      renderModule();
    }
  }, [loadedModule, ref.current]);

  return createElement(ModuleContainer, {
    ref,
    containerId,
    className: moduleClassNames,
    style,
    error: renderError,
    errorComponent,
    loadingComponent,
    appName: appName.current,
    mainScriptId,
    moduleProps: props,
  });
});

Module.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string.isRequired,
  host: PropTypes.string.isRequired,
  path: PropTypes.string,
  root: PropTypes.string,
  style: PropTypes.shape(),
  vendor: PropTypes.bool,
  className: PropTypes.string,
  loadingComponent: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.elementType,
  ]),
  errorComponent: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.elementType,
  ]),
  isolated: PropTypes.bool,
  manifestCache: PropTypes.bool,
  restClientConfig: PropTypes.shape(),
  shadowRootConfig: PropTypes.shape(),
  manifestStoreConfig: PropTypes.shape({
    resetFn: PropTypes.func,
    attempts: PropTypes.number,
  }),
};

Module.defaultProps = {
  id: "default",
  path: "",
  root: "/modules",
  style: {},
  vendor: false,
  className: "",
  loadingComponent: null,
  errorComponent: null,
  isolated: true,
  manifestCache: true,
  restClientConfig: undefined,
  shadowRootConfig: undefined,
  manifestStoreConfig: {
    resetFn: undefined,
  },
  lifeCycleEvents: undefined,
};

Module.displayName = "Module";

module.exports = Module;
